import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import queryString from 'query-string';
import numbro from 'numbro';

import InstallButtonWithPermission from 'components/InstallButtonWithPermission';
import DetailsTable from 'components/DetailsTable';
import ImageContainer from 'components/ImageContainer';
import Layout from 'components/Layout';
import LinkRow from 'components/LinkRow';
import LoadingPage from 'components/LoadingPage';
import MediaCard from 'components/MediaCard';
import ReportModal from 'components/ReportModal';
import SwiperPanel from 'components/SwiperPanel';
import Ratings from 'components/Ratings';
import {
  RatingTransition,
  ConnectedRatingInput,
  ConnectedUnrateInput
} from 'components/Ratings/RatingInput';
import {
  BasicContentBlock,
  ContentBlock,
  Icon,
  SectionTitle
} from 'components/Common';
import { fitbitGreen, fontSemibold } from 'themes';
import { os, isiOS, locale } from 'config';
import { isValidContentString, payloadFromApp } from 'utils';
import i18n, { translations, getFallbackLocale, formatDate } from 'i18n';
import fsc from 'fsc';

import creditCardIcon from 'icons/credit-card.svg';
import { UninstallModal } from './InstallButtonWithPermission/UninstallModal';
import PermissionWarningIcon from 'components/Permissions/WarningIcon';

// Regular Expression for URL validation
//
// Author: Diego Perini
// Source: https://gist.github.com/dperini/729294
// Created: 2010/12/05
// Updated: 2018/09/12
// License: MIT
//
// Copyright (c) 2010-2018 Diego Perini (http://www.iport.it)
//
// Permission is hereby granted, free of charge, to any person
// obtaining a copy of this software and associated documentation
// files (the "Software"), to deal in the Software without
// restriction, including without limitation the rights to use,
// copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the
// Software is furnished to do so, subject to the following
// conditions:
//
// The above copyright notice and this permission notice shall be
// included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
// OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
// NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
// HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
// WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
// FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
// OTHER DEALINGS IN THE SOFTWARE.
//
// Modified slightly to avoid trailing punctuation as per:
//   https://gist.github.com/dperini/729294#gistcomment-2617741
const URL_REGEX = new RegExp(
  '(' +
    // protocol identifier
    '(?:(?:https?|ftp)://)' +
    // user:pass authentication
    '(?:\\S+(?::\\S*)?@)?' +
    '(?:' +
    // IP address exclusion
    // private & local networks
    '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
    '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
    '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
    // IP address dotted notation octets
    // excludes loopback network 0.0.0.0
    // excludes reserved space >= 224.0.0.0
    // excludes network & broacast addresses
    // (first & last IP address of each class)
    '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
    '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
    '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
    '|' +
    // host & domain names
    '(?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+' +
    // TLD identifier
    '(?:[a-z\\u00a1-\\uffff]{2,})' +
    ')' +
    // port number
    '(?::\\d{2,5})?' +
    // resource path, excluding a trailing punctuation mark
    '(?:[/?#](?:\\S*[^\\s!"\'()*,-.:;<>?\\[\\]_`{|}~]|))?' +
    ')',
  'gi'
);

const Page = styled.div`
  box-sizing: border-box;
  min-height: 100%;
  width: 100%;
`;

const NoticeContainer = styled.div`
  background-color: white;
  && {
    padding: ${({ theme }) => theme.edgeDistance};
  }
`;

const NoticeText = styled.div`
  padding: 15px;
  color: ${(props) => props.color};
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${({ theme }) => theme.buttonRadius};
`;

const WarningText = styled.div`
  font-size: 13px;
`;

const Notice = (props) => {
  const color = props.color || 'white';
  const backgroundColor = props.backgroundColor || '#ee3d6e';
  return (
    <NoticeContainer>
      <NoticeText color={color} backgroundColor={backgroundColor}>
        {props.children}
      </NoticeText>
    </NoticeContainer>
  );
};

const NoticeLink = styled.a`
  ${({ newLine }) =>
    newLine
      ? css`
          display: block;
          margin-top: 5px;
        `
      : ''}
  white-space: nowrap;
  text-decoration: underline;

  && {
    border-bottom: none;
  }
`;

const RemoveButton = styled(ContentBlock)`
  border: 1px solid #d8d8d8;
  color: red;
  cursor: pointer;
  font-size: 17px;
  margin: 20px 0;
  text-align: center;
`;

const MainContent = styled.div`
  background: white;
  ${({ theme }) => theme.AppMainContent};
`;

const ReportText = styled.p`
  color: #767676;
  font-size: 14px;
  margin: 10px ${({ theme }) => theme.edgeDistance} 0;
  padding-bottom: 60px;
`;

const StyledLink = styled.a`
  color: ${fitbitGreen};
`;

const ReportTextLink = StyledLink;

const ContactLink = StyledLink;

const Container = ({ backText, children }) => (
  <Layout
    backText={backText}
    name="Details"
    scrollKey="details"
    pageElement={<Page className="app-container">{children}</Page>}
  />
);

const DescriptionContainer = styled.div`
  p {
    word-break: break-word;
    font-family: 'Proxima Nova Light';

    &:first-of-type {
      margin-top: 0;
    }
    &:last-of-type {
      margin-bottom: 0;
    }
  }
  a {
    color: #0000ff;
    &:visited {
      color: #800080;
    }
    &:active {
      color: #ff0000;
    }
  }
`;

const Description = styled.div`
  height: 300px;
  overflow: hidden;
  position: relative;
`;

const ShowMoreContainer = styled.div`
  text-align: center;
  width: 100%;
`;

const ShowMore = styled.div`
  color: ${fitbitGreen};
  border: 1px solid #d8d8d8;
  border-radius: 5px;
  font: 14px ${fontSemibold};
  display: inline-block;
  margin: 0 auto;
  padding: 3px 5px;
  text-align: center;
  width: 100px;
`;

const Hidden = styled.div`
  background-image: linear-gradient(
    -180deg,
    rgba(255, 255, 255, 0) 0%,
    white 100%
  );
  height: 100px;
  position: absolute;
  bottom: 0;
  width: 100%;
`;

const PaidBannerSection = styled.span`
  ${({ theme }) => `
    display: flex;
    border: 1px solid ${theme.fitbitGreen};
    border-radius: ${isiOS ? 6 : 2}px;
    margin: ${theme.edgeDistance};
    padding: 16px;
    color: ${fitbitGreen};
    font-size: 13px;
    font-family: ${theme.fontRegular};
  `}
`;

const PaidBannerText = styled.div`
  display: inline-block;
`;

const CreditCardImg = styled.img`
  margin-right: 12px;
  vertical-align: middle;
`;

const PermissionLinkWrapper = styled.div`
  ${({ theme }) => theme.PermissionLinkWrapper}
`;

const AFTER_DETAILS = 'AFTER_DETAILS';

const DeveloperContactButton = ({ app, phoneAppVersion, label, showLabel }) => {
  const { buildId, contactUrl, contactEmail, id, name, versionString } = app;

  const handleContactBtnClicked = () => {
    const { app } = this.props;
    fsc
      .view(`${app.isClock ? 'Clock' : 'App'} Details`)
      .Tapped('Contact Developer')
      .withPayload(payloadFromApp(app))
      .send();
  };

  if (contactUrl || contactEmail) {
    let url = contactUrl;
    if (contactUrl && contactUrl.startsWith('http')) {
      const urlObject = new URL(contactUrl);
      const params = queryString.parse(urlObject.search);
      urlObject.search = queryString.stringify({
        applicationId: id,
        applicationName: name,
        buildId,
        hostOS: os,
        hostAppVersion: phoneAppVersion || 'N/A',
        versionString,
        ...params
      });
      url = urlObject.toString();
    } else if (contactEmail) {
      url = `mailto:${contactEmail}?${queryString.stringify({
        subject: `Support request for Fitbit application: ${name}`
      })}`;
    }
    return (
      <ContactLink href={url} target="_blank" onClick={handleContactBtnClicked}>
        {label}
      </ContactLink>
    );
  } else if (showLabel) {
    return <span>{label}</span>;
  }
  return <React.Fragment />;
};

class AppDetails extends Component {
  static defaultProps = {
    appTypeName: 'App'
  };

  state = {
    showAll: false,
    showReportModal: false,
    showUninstallModal: false
  };

  navigateToSettings = (event) => {
    event.preventDefault();
    const { app } = this.props;
    app.navigateToSettings();
  };

  showAll = () => {
    this.setState({ showAll: true });
  };

  openReportModal = () => {
    this.setState({ showReportModal: true });
  };

  closeReportModal = () => {
    this.setState({ showReportModal: false });
  };

  openUninstallModal = () => {
    this.setState({ showUninstallModal: true });
  };

  closeUninstallModal = () => {
    this.setState({ showUninstallModal: false });
  };

  renderDetails() {
    const { app, detailsSectionTitle, phoneAppVersion } = this.props;
    const {
      component: {
        AppDetails: {
          ColumnDescriptor: {
            updated: updatedColDesc,
            size: sizeColDesc,
            version: versionColDesc,
            developer: developerColDesc,
            status: statusColDesc
          },
          privateStatusText: privateDesc,
          publicStatusText: publicDesc,
          contactDeveloper
        }
      }
    } = translations;
    const details = [];
    if (app.updatedDate) {
      details.push({
        col1: updatedColDesc(),
        col2: formatDate(app.updatedDate)
      });
    }
    if (app.size) {
      details.push({
        col1: sizeColDesc(),
        col2: numbro(app.size).format({
          output: 'byte',
          base: 'binary',
          mantissa: 2
        })
      });
    }
    if (app.versionString) {
      details.push({
        col1: versionColDesc(),
        col2: app.versionString
      });
    }
    if (app.isPrivate) {
      details.push({
        col1: statusColDesc(),
        col2: app.isPrivate ? privateDesc() : publicDesc()
      });
    }
    details.push({
      col1: developerColDesc(),
      col2: (
        <span>
          {app.developer.name}
          <div>
            <DeveloperContactButton
              app={app}
              phoneAppVersion={phoneAppVersion}
              label={contactDeveloper()}
            />
          </div>
        </span>
      )
    });

    return (
      <div>
        <SectionTitle>{detailsSectionTitle}</SectionTitle>
        <ContentBlock borderTop={true} borderBottom={true}>
          <DetailsTable data={details} />
        </ContentBlock>
        {this.renderRichDescription(AFTER_DETAILS)}
      </div>
    );
  }

  renderRichDescription(position) {
    const { app } = this.props;
    if (!app.richDescription) {
      return;
    }

    if (app.richDescriptionPosition === position) {
      return <div dangerouslySetInnerHTML={{ __html: app.richDescription }} />;
    }
  }

  renderDescription = () => {
    const {
      app: { description = '' }
    } = this.props;
    const { showAll } = this.state;
    const {
      component: {
        AppDetails: { showMoreButtonText }
      }
    } = translations;

    const parsedDescription = description
      .trim()
      .split('\n')
      .filter((paragraph) => isValidContentString(paragraph))
      .map((paragraph, index) => {
        const urls = paragraph.match(URL_REGEX);
        if (urls && urls.length > 0) {
          paragraph = paragraph.split(URL_REGEX).map((element, pIndex) => {
            if (urls.includes(element)) {
              return (
                <a
                  href={element}
                  key={`${index}${pIndex}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {element}
                </a>
              );
            }
            return element;
          });
        }
        return <p key={index}>{paragraph}</p>;
      });

    if (isValidContentString(description, { minLength: 530 }) && !showAll) {
      return (
        <DescriptionContainer>
          <Description>
            {parsedDescription}
            <Hidden />
          </Description>
          <ShowMoreContainer>
            <ShowMore onClick={this.showAll}>{showMoreButtonText()}</ShowMore>
          </ShowMoreContainer>
        </DescriptionContainer>
      );
    }

    return <DescriptionContainer>{parsedDescription}</DescriptionContainer>;
  };

  renderPaidBanner = () => {
    const {
      component: {
        AppDetails: { paidBannerText }
      }
    } = translations;

    return (
      <PaidBannerSection>
        <CreditCardImg src={creditCardIcon} alt="Payment" />
        <PaidBannerText>{paidBannerText()}</PaidBannerText>
      </PaidBannerSection>
    );
  };

  renderInstallButton = () => {
    const { app, installTexts, showRemove } = this.props;

    return (
      <InstallButtonWithPermission
        app={app}
        showRemove={showRemove}
        texts={installTexts}
      />
    );
  };

  renderRemoveButton = () => {
    const { app, showRemove } = this.props;
    const {
      component: {
        AppDetails: { removeButtonText }
      }
    } = translations;
    if (showRemove && app.isOnDevice && isiOS && !app.cannotInstallOnDevice) {
      return (
        <RemoveButton onClick={this.openUninstallModal} borderTop={true}>
          {removeButtonText()}
        </RemoveButton>
      );
    }
  };

  renderImage = () => {
    const { banners = [] } = this.props.app;

    return <SwiperPanel banners={banners} />;
  };

  renderPermissionIssueNotice = () => {
    const {
      permissionIssueNoticeSingluar,
      permissionIssueNoticePlural,
      permissionIssueNoticeNameJoiner,
      permissionIssueNoticeLink
    } = translations.page.App.Details;

    const { app } = this.props;

    if (app.isOnDevice && app.hasPermissionWarning) {
      const permissionNames = app.permissionsWithIssues.map((name) =>
        i18n(`comms.permissions.${name}.label`)()
      );
      // Link to help article to troubleshoot app issues
      const link = `https://help.fitbit.com/articles/Help_article/2275?l=${getFallbackLocale(
        locale
      ).replace('-', '_')}#troubleshoot`;

      return (
        <Notice>
          {permissionNames.length > 1
            ? permissionIssueNoticePlural({
                permissionNames: permissionNames.join(
                  ` ${permissionIssueNoticeNameJoiner()} `
                )
              })
            : permissionIssueNoticeSingluar({
                permissionNames: permissionNames.join(
                  ` ${permissionIssueNoticeNameJoiner()} `
                )
              })}{' '}
          <NoticeLink href={link} newLine>
            {permissionIssueNoticeLink()}
          </NoticeLink>
        </Notice>
      );
    }
  };

  renderCannotInstallNotice = () => {
    const {
      app,
      cannotInstallNotice,
      cannotInstallNoticeNoVersion,
      cannotInstallNoticeNoVersionLink,
      cannotInstallNoticeDeviceDisconnected,
      restrictedRegionMessage
    } = this.props;

    let notice;

    if (app.hasNoVersionAvailable) {
      if (!app.availableForDevice) {
        // Not compatible with this device type
        notice = cannotInstallNotice;
      } else {
        // Compatible with device, but not FW or mobile version
        const link = `https://help.fitbit.com/articles/en_US/Help_article/1871/?l=${getFallbackLocale(
          locale
        ).replace('-', '_')}`;
        notice = (
          <div>
            {cannotInstallNoticeNoVersion}{' '}
            <NoticeLink href={link} newLine>
              {cannotInstallNoticeNoVersionLink}
            </NoticeLink>
          </div>
        );
      }
    } else if (app.isRestrictedForUserRegion()) {
      notice = restrictedRegionMessage;
    } else if (app.cannotInstallOnDevice) {
      notice = cannotInstallNoticeDeviceDisconnected;
    }

    if (notice) {
      return <Notice>{notice}</Notice>;
    }
  };

  renderModal = () => {
    const { app, showHiggsCU2 } = this.props;

    const { showReportModal } = this.state;

    if (showHiggsCU2 && showReportModal) {
      return <ReportModal app={app} onClose={this.closeReportModal} />;
    }
  };

  renderReport = () => {
    const {
      component: {
        AppDetails: { reportText, reportLinkText }
      }
    } = translations;

    return (
      <ReportText>
        {reportText()}&nbsp;
        <ReportTextLink onClick={this.openReportModal}>
          {reportLinkText()}
        </ReportTextLink>
      </ReportText>
    );
  };

  renderRatings = () => {
    const { app } = this.props;
    const {
      component: {
        Ratings: { rateThisItemText, ratingText }
      }
    } = translations;

    if (!app.showRatings) {
      return null;
    }

    return (
      <div>
        <RatingTransition show={app.showRatingInput}>
          <SectionTitle>{rateThisItemText()}</SectionTitle>
          <BasicContentBlock
            borderTop
            borderBottom
            style={{ paddingTop: '14px', paddingBottom: '14px' }}
          >
            <ConnectedRatingInput app={app} />
          </BasicContentBlock>
        </RatingTransition>
        <div>
          <SectionTitle>{ratingText()}</SectionTitle>
          <BasicContentBlock
            borderTop
            borderBottom
            style={{ paddingTop: '25px', paddingBottom: '25px' }}
          >
            <Ratings app={app} />
          </BasicContentBlock>
        </div>
        {app.userRating && (
          <BasicContentBlock
            borderBottom
            style={{ paddingTop: '14px', paddingBottom: '14px' }}
          >
            <ConnectedUnrateInput app={app} />
          </BasicContentBlock>
        )}
      </div>
    );
  };

  renderWarning() {
    const { app, phoneAppVersion } = this.props;

    if (!app.compatibilityMode) {
      return;
    }

    const {
      page: {
        App: {
          Details: { compatibilityNotice, compatibilityNoticeLink }
        }
      }
    } = translations;

    return (
      <Notice color={'#707C83'} backgroundColor={'#E7E9EA'}>
        <WarningText>
          {compatibilityNotice({ type: app.isClock ? 'clock' : 'app' })}
          <DeveloperContactButton
            app={app}
            phoneAppVersion={phoneAppVersion}
            label={compatibilityNoticeLink()}
            showLabel={true}
          />
        </WarningText>
      </Notice>
    );
  }

  onUninstall = () => {
    const { uninstallApp } = this.props;
    uninstallApp && uninstallApp();
    this.closeUninstallModal();
  };

  render() {
    const {
      loading,
      app,
      backText,
      descriptionSectionTitle,
      showHiggsCU2
    } = this.props;
    const {
      component: {
        AppDetails: { settingsLinkText, permissionLinkText }
      }
    } = translations;

    if (loading || !app) {
      return (
        <Container backText={backText}>
          <LoadingPage headerHeight={56} />
        </Container>
      );
    }

    return (
      <Container backText={backText}>
        {this.state.showUninstallModal && (
          <UninstallModal
            appName={app.name}
            onConfirm={this.onUninstall}
            onClose={this.closeUninstallModal}
          />
        )}
        {this.renderModal()}
        {this.renderImage()}
        <MainContent>
          <div>
            <MediaCard
              border={!isiOS}
              leftItem={
                !app.isClock ? (
                  <ImageContainer
                    containerAttr={{
                      iconPlaceholder: true
                    }}
                    imageAttr={{
                      alt: app.name,
                      src: app.icon.uri,
                      styles: Icon
                    }}
                  />
                ) : (
                  undefined
                )
              }
              title={app.name}
              subtitle={app.developer.name}
              isPrivate={app.isPrivate}
              rightItem={this.renderInstallButton()}
            />
            {this.renderCannotInstallNotice()}
            {this.renderWarning()}
          </div>
          {app.isOnDevice && app.hasSettings && (
            <LinkRow
              text={settingsLinkText()}
              onClick={this.navigateToSettings}
            />
          )}
          {app.isOnDevice &&
            (app.hasGrantedPermissions || app.hasPermissionWarning) && (
              <LinkRow
                pageDirection="FORWARD"
                text={
                  <PermissionLinkWrapper>
                    {permissionLinkText()}
                    {app.hasPermissionWarning && <PermissionWarningIcon />}
                  </PermissionLinkWrapper>
                }
                to={`/${app.isClock ? 'clock' : 'app'}/${app.id}/permissions`}
              />
            )}
          {this.renderPermissionIssueNotice()}
          {app.isPaid && this.renderPaidBanner()}
        </MainContent>
        {this.renderRemoveButton()}
        {this.renderRatings()}
        {isValidContentString(app.description) && (
          <div>
            <SectionTitle>{descriptionSectionTitle}</SectionTitle>
            <ContentBlock borderTop={true} borderBottom={true}>
              {this.renderDescription()}
            </ContentBlock>
          </div>
        )}
        {this.renderDetails()}
        {showHiggsCU2 && this.renderReport()}
      </Container>
    );
  }
}

export default AppDetails;
